// src/usePermissions.js
import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { supabase } from './supabaseClient';

export default function usePermissions() {
  const { accounts } = useMsal();
  const [permissions, setPermissions] = useState({});
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const noPermissions = {
      profiles: false,
      closet_items: false,
      bookings: false,
      blocked_users: false,
      payments: false,
      payouts: false,
      chats: false,
      contact_us: false,
      brands: false,
      sizes: false,
      body_types: false,
      style_preferences: false,
      categories: false,
      subcategories: false,
      subscriptions: false,
      faqs: false,
      issues: false,
      activity_logs: false,
      system_settings: false,
      user_management: false,
    };

    // Prefer Supabase Auth email (this is what RLS uses)
    const { data: sessionData } = await supabase.auth.getSession();
    const sbEmail = sessionData?.session?.user?.email || null;

    // Fallback to MSAL account email only for legacy flows
    const msalEmail = accounts?.[0]?.username || accounts?.[0]?.name || null;

    const userEmail = (sbEmail || msalEmail || '').trim();

    if (!userEmail) {
      setPermissions(noPermissions);
      setRole(null);
      setLoading(false);
      return;
    }

    try {
      
      const { data, error } = await supabase
        .from('admin_users')
        .select('role, permissions, active')
        .ilike('microsoft_email', userEmail)
        .eq('active', true)
        .maybeSingle();

      if (error || !data) {
        setPermissions(noPermissions);
        setRole(null);
        setLoading(false);
        return;
      }

      setRole(data.role || null);
      setPermissions(data.permissions || noPermissions);
      
      // Update last login
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('microsoft_email', userEmail);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setPermissions(noPermissions);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [accounts]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = (permission) => {
    // Super admins have all permissions
    if (role === 'super_admin') return true;
    
    // Check specific permission
    return permissions[permission] === true;
  };

  const canManageUsers = () => {
    return role === 'super_admin' || hasPermission('user_management');
  };

  return {
    permissions,
    role,
    loading,
    hasPermission,
    canManageUsers,
    isSuperAdmin: role === 'super_admin',
    isAdmin: role === 'super_admin' || role === 'admin',
  };
}

